import { useEffect, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { db } from '../../../config/firebase';

/**
 * Live Video Wall sync telemetry (Phase 1.5).
 *
 * Subscribes to RTDB `{ENV}/VideoWalls/{wallId}` and surfaces the coordinated-
 * start state + per-screen drift each player publishes once per second. Uses the
 * shared `db` (anonymous auth is bootstrapped in src/config/firebase.js), the
 * same pattern as FirebaseDeviceListener. Returns:
 *
 *   {
 *     state,                       // "preparing" | "playing" | "stopped" | ""
 *     expectedScreens,
 *     readyCount,
 *     cachingScreens,              // # screens with cacheProgress < 100
 *     syncMode,                    // "lan" | "cloud" | "" — Phase 4 LAN vs Firebase clock
 *     masterPos,                   // elected master's grid_position (Phase 4), or null
 *     screens: { [gridPosition]: { drift_ms, playback_speed, lastSync, ready, cacheProgress,
 *                                  sync_mode, master_pos, lan_offset_ms } },
 *   }
 */
export default function useWallSyncTelemetry(wallId) {
  const [telemetry, setTelemetry] = useState({
    state: '',
    expectedScreens: 0,
    readyCount: 0,
    cachingScreens: 0,
    syncMode: '',
    masterPos: null,
    screens: {},
  });

  useEffect(() => {
    if (!wallId || !db) return undefined;
    const path = `${process.env.REACT_APP_APP_ENV}/VideoWalls/${wallId}`;
    const node = ref(db, path);

    const unsubscribe = onValue(node, (snapshot) => {
      const val = snapshot.val() || {};
      const screens = val.screens || {};
      const list = Object.values(screens).filter(Boolean);
      const readyCount = list.filter((s) => s.ready).length;
      const cachingScreens = list.filter((s) => (s.cacheProgress ?? 100) < 100).length;
      // Phase 4: the wall is on LAN sync only when EVERY reporting screen says so;
      // any screen still on the Firebase clock means we surface "cloud fallback".
      const reporters = list.filter((s) => s.sync_mode);
      const syncMode = reporters.length === 0
        ? ''
        : (reporters.every((s) => s.sync_mode === 'lan') ? 'lan' : 'cloud');
      const masterPos = reporters.find((s) => s.master_pos != null)?.master_pos ?? null;
      setTelemetry({
        state: val.state || '',
        expectedScreens: val.expected_screens || 0,
        readyCount,
        cachingScreens,
        syncMode,
        masterPos,
        screens,
      });
    });

    return () => unsubscribe();
  }, [wallId]);

  return telemetry;
}

/** 🟢 <50ms · 🟡 <150ms · 🔴 ≥150ms · grey = no telemetry / not playing.
 *  Phase 3: tightened from 100/300 so the dot reflects what the eye sees on
 *  motion content (a 3-frame inter-screen Δ is visible on a rotating object). */
export function syncHealth(driftMs) {
  if (driftMs == null) return 'unknown';
  const d = Math.abs(driftMs);
  if (d < 50) return 'healthy';
  if (d < 150) return 'drifting';
  return 'out_of_sync';
}

export const HEALTH_COLORS = {
  healthy: 'var(--pp-success)',
  drifting: 'var(--pp-warning)',
  out_of_sync: 'var(--pp-danger, #ef4444)',
  unknown: 'var(--pp-offline, #94a3b8)',
};
